(ns app.front.view.application-wizard.parties
  "Parties to Application step for the application wizard.
   Includes a list of required/other parties, placeholder and filled cards,
   and inline add/edit flow for Person, Contractor, and Business parties."
  (:require ["@carbon/icons-react/es/building/32" :default Building32]
            ["@carbon/icons-react/es/collapse-all/16" :default CollapseAll16]
            ["@carbon/icons-react/es/construction/32" :default Construction32]
            ["@carbon/icons-react/es/expand-all/16" :default ExpandAll16]
            ["@carbon/icons-react/es/user/32" :default User32]
            ["@carbon/icons-react/es/view/16" :default View16]
            [app.domain.business.model :as business-model]
            [app.domain.contractor-registration.model :as cr-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.requirement.model :as req-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.process.save-tracking :as save-tracking]
            [app.front.ui :as ui]
            [app.front.ui.dropdown :as ui-dropdown]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.select :as ui-select]
            [app.front.ui.v2.checkbox-picker :as ui-checkbox-picker]
            [app.front.ui.v2.composed-card :as ui-card]
            [app.front.ui.v2.status-tag :as ui-status-tag]
            [app.front.util :as futil]
            [app.front.validate :as validate]
            [app.front.view.business.information :as business-info-form]
            [app.front.view.party :as party-view]
            [app.front.view.party.assign-role :as assign-role]
            [app.front.view.party.business :as party-business]
            [app.front.view.party.common :as party-common]
            [app.front.view.party.edit :as party-edit]
            [app.front.view.party.registered-contractor :as party-registered-contractor]
            [app.front.view.party.requirements :as party-requirements]
            [app.front.view.party.subs :as party-subs]
            [app.front.view.party.subs-req :as party-req-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.normalize :as normalize]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::local-expanded-party-ids
  (fn [db _]
    (get db ::local-expanded-party-ids #{})))

(rf/reg-event-db ::toggle-local-expanded-party-id
  (fn [db [_ party-id]]
    (update db ::local-expanded-party-ids util/toggle-in-set party-id)))

(rf/reg-event-db ::set-all-parties-expanded
  (fn [db [_ party-ids expand?]]
    (assoc db ::local-expanded-party-ids (if expand? (set party-ids) #{}))))

(rf/reg-sub ::editing-state :-> ::editing-state)

(rf/reg-event-db ::set-editing-state
  (fn [db [_ value]]
    (assoc db ::editing-state value)))

(rf/reg-event-fx ::close-inline-form
  (fn [{:keys [db]} _]
    {:db         (dissoc db ::editing-state)
     :dispatch-n [[::party-common/close]
                  [::party-registered-contractor/clear]]}))

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} _]
    {:db         (dissoc db ::editing-state ::local-expanded-party-ids ::add-required-role)
     :dispatch-n [[::party-common/close]
                  [::party-registered-contractor/clear]]}))

(rf/reg-sub ::add-required-role
  (fn [db [_ k]]
    (if k
      (get-in db [::add-required-role k])
      (get db ::add-required-role))))

(rf/reg-event-db ::open-add-required-role
  (fn [db _]
    (assoc db ::add-required-role {})))

(rf/reg-event-db ::set-add-required-role
  (fn [db [_ role]]
    (assoc-in db [::add-required-role :role] role)))

(rf/reg-event-db ::close-add-required-role
  (fn [db _]
    (dissoc db ::add-required-role)))

(rf/reg-event-fx ::confirm-add-required-role
  (fn [{:keys [db]} [_ phase-id]]
    (when-let [role-id (get-in db [::add-required-role :role :id])]
      {:db       (dissoc db ::add-required-role)
       :dispatch [::process/signal
                  phase-id
                  [:process.signal/add-party-role-requirement {:role-id role-id}]
                  {:notify {:success :party/required-role-added}}]})))

(rf/reg-event-fx ::enter-step
  (fn [_ _]
    ;; The applicant picker offers the businesses of the user, so they are needed on entry
    ;; (the add-party form fetches them again when it is opened).
    {:dispatch [::party-business/fetch-my-businesses]}))

(defn- editing-creating? [editing]
  (= :creating (:mode editing)))

(defn- editing-party-id [editing]
  (:party-id editing))

;; NOTE: As of #7940 a phase needing pre-consultation exits the wizard to the workspace page
;; right after the location step, so in practice the parties step only runs for applications and
;; the pre-consultation branch (and its role requirements) is never reached here. Kept anyway in
;; case the flow changes and pre-consultation parties are handled in the wizard again.
(rf/reg-sub ::phase
  :<- [::ws-subs/application]
  :<- [::ws-subs/pre-consultation]
  (fn [[application pre-consultation] _]
    (or application pre-consultation)))

;; Mirrors the backend submit guard `required-party-roles-except-deferred`: deferred-role
;; requirements stop counting as missing once explicitly deferred (:requirement/deferred-by),
;; and only roles applicable per the workspace config are considered.
(rf/reg-sub ::missing-roles
  :<- [::phase]
  (fn [phase _]
    (some-> phase
            (party-model/missing-roles req-model/all-role-requirement-kinds true))))

;;;
;;; Applicant selection: apply for yourself or for one of your businesses
;;;

(rf/reg-sub ::own-applicant-party
  ;; The applicant party the user acts as - the one the applicant selection replaces.
  (fn [[_ phase-id]]
    [(rf/subscribe [::party-subs/parties phase-id])
     (rf/subscribe [:session/user])])
  (fn [[parties user] _]
    (let [applicants (filter #(and (party-model/user-can-act-as-party? user %)
                                   (party-model/has-role? % :role/applicant))
                             parties)]
      (or (p/find-first applicants :party/workspace-creator?)
          (first applicants)))))

(rf/reg-sub ::applicant-options
  (fn [_]
    [(rf/subscribe [::party-business/all-my-businesses])
     (rf/subscribe [:session/user])
     (rf/subscribe [:language])
     (rf/subscribe [:tr])])
  (fn [[businesses user lang tr] _]
    ;; Include businesses already added as parties. Selecting one promotes that existing party
    ;; instead of creating a duplicate.
    ;; Option labels mirror the classic wizard applicant picker.
    (->> businesses
         (map (fn [business]
                {:business/unique-id (:business/unique-id business)
                 :name               (str (business-model/format-name business lang)
                                          " (" (tr [:party.type/business]) ")")}))
         (sort-by :name)
         (cons {:name (str (tr [:wizard/myself]) " (" (user-model/full-name user) ")")})
         vec)))

(rf/reg-sub ::selected-applicant-option
  (fn [[_ phase-id]]
    [(rf/subscribe [::applicant-options phase-id])
     (rf/subscribe [::own-applicant-party phase-id])])
  (fn [[options applicant] _]
    ;; A party without a business is the user themselves, matching the option without a business.
    (let [unique-id (-> applicant :party/business :business/unique-id)]
      (p/find-first options #(= unique-id (:business/unique-id %))))))

(rf/reg-event-fx ::select-applicant
  ;; The draft already has an applicant party, so changing the selection replaces it: the backend
  ;; removes the current party and adds the selected applicant with the same roles.
  (fn [_ [_ phase-id party-id {:business/keys [unique-id]}]]
    {:dispatch [::process/signal
                phase-id
                [:process.signal/change-applicant
                 (p/assoc-if {:party-id party-id} :business/unique-id unique-id)]
                {:notify      {:success :changes-saved}
                 :track-save? true}]}))

(defn- valid-person-party? [party]
  ;; model-first validation derives required fields from form-model, so the
  ;; completeness check always matches what the party edit form enforces
  (let [party (party-model/merge-user-and-party party)]
    (empty? (validate/validate-form-strict party party-common/form-model))))

(defn- business-party-complete? [party lang]
  (let [business (:party/business party)]
    (and business
         (:business/type business)
         (not (str/blank? (business-model/business-name business lang)))
         (:business/subdivision business)
         (seq (:party/party-roles party)))))

(defn- registered-contractor-party? [party]
  (or (:party/registration party)
      (seq (cr-model/party-entity->registrations party))))

(defn- contractor-party-complete? [party]
  (seq (:party/party-roles party)))

(defn party-complete?
  "Completeness check for a single party in the wizard parties step:
   would the corresponding edit form pass validation?"
  [lang party]
  (cond
    (registered-contractor-party? party) (contractor-party-complete? party)
    (:party/business party) (business-party-complete? party lang)
    :else (valid-person-party? party)))

(rf/reg-sub ::step-completed?
  :<- [::phase]
  :<- [::missing-roles]
  (fn [[phase missing-roles] _]
    (boolean (and phase (empty? missing-roles)))))

(defn role-pills-display [party lang]
  (let [[first-role second-role & others] (->> party :party/party-roles (map #(role-model/party-role-name % lang)))]
    (str/join " | " (keep identity [first-role second-role (some->> (seq others) count (str "+"))]))))

(defn- init-add-party-state
  "Starts the create flow. :required-role-id marks the placeholder flow (REQUIRED banner,
   locked role checkbox, role-compatible radios); :party-type preselects a type (Add menu or
   a single-compatible-type role — otherwise the type radios start unselected)."
  [{:keys [fixed-contractor-role? required-role-id required-trade-name party-type]}]
  (>evt [::set-editing-state (p/assoc-if {:mode :creating}
                                         :fixed-contractor-role? fixed-contractor-role?
                                         :required-role-id required-role-id
                                         :required-trade-name required-trade-name)])
  (>evt [::party-common/init :invite (p/assoc-if {::party-common/manual-mode? false}
                                                 :party/type        party-type
                                                 :party/party-roles (some-> required-role-id vector))])
  (>evt [::party-business/init])
  ;; Load the user's existing businesses so the business form can offer them for selection
  ;; (mirrors the classic party_section_v2 add-party flow).
  (>evt [::party-business/fetch-my-businesses])
  (>evt [::party-registered-contractor/init required-trade-name]))

(defn- add-party-type-values [display-cr-option? display-business-option?]
  (remove nil?
          [:party.type/person
           (when display-business-option? :party.type/business)
           (when display-cr-option? :party.type/registered-contractor)]))

(defn- role-compatible-party-types [process types role-id]
  (filterv #(party-common/party-operation-permitted? process :invite % role-id) types))

(defn- party-type [party]
  (cond
    (registered-contractor-party? party) :party.type/registered-contractor
    (:party/business party) :party.type/business
    :else :party.type/person))

;; Scalar (non-localized) editable business attributes, derived from the centralized attribute list.
(def ^:private editable-business-scalar-attrs
  (remove #{:business/names :business/trade-names}
          business-model/business-attrs))

(defn- business->document-attrs
  "Flatten an existing business entity into the top-level document keys that `business-info-form`
   reads via its value-fn. Localized strings are converted to the lang->string map the editor expects."
  [business]
  (-> (select-keys business editable-business-scalar-attrs)
      (p/assoc-if :business/names (some-> (:business/names business) i18n-model/strings-map))
      (p/assoc-if :business/trade-names (some-> (:business/trade-names business) i18n-model/strings-map))))

(defn- init-edit-party-state [party]
  (let [party-type (party-type party)
        ;; ->map is required: for parties without :party/user this is still an entity, which throws on assoc
        base       (-> (eu/->map (party-model/merge-user-and-party party))
                       (assoc :party/type party-type))]
    (>evt [::set-editing-state {:mode       :editing
                                :party-id   (:db/id party)
                                :party-type party-type}])
    (>evt [::party-edit/init (cond-> base
                               (= party-type :party.type/business)
                               (merge (business->document-attrs (:party/business party))))])))

(defn- placeholder-action-button [tr {:keys [icon label on-click]}]
  (cc/button {:kind          :ghost
              :size          :small
              :icon          icon
              :icon-position :left
              :data-cy       (name label)
              :class-name    "justify-start normal-case whitespace-nowrap text-left text-ui-violet-100 font-normal"
              :on-click      on-click}
             (tr [label])))

(defn- placeholder-card [tr phase requirement title compatible-party-types]
  (let [phase-id            (:db/id phase)
        role                (:requirement/party-role requirement)
        preselected-role-id (:db/id role)
        required-trade      (:party-role/trade role)
        required-trade-name (some-> required-trade
                                    :group.config/names
                                    (i18n-model/get-text-from-entities-or-map (<sub [:language])))
        can-remove?         (<sub [::process/signal-permitted?
                                   phase-id
                                   [:process.signal/remove-party-role-requirement
                                    {:requirement-id (:db/id requirement)}]])]
    [ui-card/card
     {:placeholder?   true
      ;; The "required" tag overlays the corner. pt-8 mirrors the footer's
      ;; vertical padding (2x p-4), centering icon + title between the top and button.
      :content-class  "flex flex-col justify-center pt-8"
      :footer-content [ui-card/row {:align :center}
                       [:div
                        {:data-guide-id          (str "assign-party-" (:db/id requirement))
                         :data-guide-label       title
                         :data-guide-description (str "Assign an existing party or add a new party"
                                                      (when required-trade " from the contractor registry")
                                                      (when can-remove? ", or remove this required role"))}
                        [ui-dropdown/dropdown
                         {:align                  "left"
                          :aria-label             (tr [:application-wizard/parties-assign-party])
                          :open-icon-description  (tr [:open-menu])
                          :close-icon-description (tr [:close-menu])
                          :data-cy                "placeholder-assign-party"
                          :class                  "bg-ui-violet-100 text-white rounded hover:bg-ui-violet-130 data-[open=true]:bg-ui-violet-130"
                          :toggle-props           {:class "gap-2 px-3 py-2 text-xs font-semibold uppercase cursor-pointer text-inherit"}
                          :options-props          {:class "w-max bg-ui-white border border-solid border-gray-300 filter drop-shadow-xl divide-y divide-gray-200"}}
                         [:span (tr [:application-wizard/parties-assign-party])]
                         (when required-trade
                           [placeholder-action-button tr
                            {:icon     i/helmet
                             :label    :party/add-party-from-cr
                             :on-click #(init-add-party-state
                                          {:fixed-contractor-role? true
                                           :required-role-id       preselected-role-id
                                           :required-trade-name    required-trade-name
                                           :party-type             :party.type/registered-contractor})}])
                         [placeholder-action-button tr
                          {:icon     i/link
                           :label    :party/assign-role-to-party
                           :on-click #(>evt [::party-common/init-assign-role preselected-role-id])}]
                         [placeholder-action-button tr
                          {:icon     i/user-plus
                           :label    :party/add-new-party
                           :on-click #(init-add-party-state
                                        {:required-role-id preselected-role-id
                                         :party-type       (when (= 1 (count compatible-party-types))
                                                             (first compatible-party-types))})}]
                         (when can-remove?
                           [placeholder-action-button tr
                            {:icon     i/trash
                             :label    :party/remove-required-role
                             :on-click #(>evt [::party-req-subs/remove-role-requirement-clicked
                                               {:requirement-id (:db/id requirement)
                                                :parent-id      phase-id}])}])]]]}
     [:div.absolute.left-5.top-4
      [ui-status-tag/tag {:status :attention} (tr [:application-wizard/parties-required])]]
     [ui-card/row {:align :center}
      [:div.h-16.w-16
       {:class "[&>svg]:w-full [&>svg]:h-full"}
       [:> User32]]]
     [ui-card/row {:align :center}
      [ui-card/title {} title]]]))

(defn- selected-roles []
  (->> (<sub [::party-common/document-party-roles])
       (sort-by :db/id)
       vec))

(defn- available-role-selector-values [target-id selected-roles]
  ;; ::party-common/available-roles only offers roles permitted by the relevant signal
  ;; (party-operation-permitted?); without a page-kind (edit mode) it checks
  ;; :process.signal/add-party-role, same as the classic role selector.
  (->> (concat selected-roles (<sub [::party-common/available-roles target-id]))
       (remove nil?)
       (sort-by party-common/party-role-label)
       vec))

(defn contractor-default-roles
  [registration roles required-role-id]
  (let [work-type-ids (set (:work-type-ids registration))]
    (->> roles
         (filter #(or (= required-role-id (:db/id %))
                      (some-> % :party-role/trade :db/id work-type-ids)))
         vec)))

(rf/reg-event-db ::select-contractor
  (fn [db [_ registration roles required-role-id]]
    (-> db
        (assoc-in [::party-common/document :party/registration] registration)
        (assoc-in [::party-common/document :party/party-roles]
                  (mapv :db/id (contractor-default-roles registration roles required-role-id))))))

(defn- toggle-create-role! [role add? process party-type]
  (rf/dispatch-sync [::party-common/update-doc-value [:party/party-roles]
                     (fn [role-ids role-id]
                       (->> (if add?
                              (distinct (concat role-ids [role-id]))
                              (remove #{role-id} role-ids))
                            (filter (partial party-common/party-operation-permitted? process :invite party-type))
                            vec))
                     (:db/id role)]))

(defn- toggle-edit-role! [target-id editing-id role add?]
  (>evt [::party-common/update-doc-value [:party/party-roles]
         (fn [role-ids role-id]
           (->> (if add?
                  (conj (set role-ids) role-id)
                  (remove #{role-id} role-ids))
                vec))
         (:db/id role)])
  (>evt [::party-edit/edit :party/party-roles
         (:db/id role)
         editing-id
         (if add? :add :remove)
         target-id]))

(defn- role-selector-panel [tr validator target editing-id required-role-id disabled?]
  (let [editing-existing? (integer? editing-id)
        target-id         (:db/id target)
        lang              (<sub [:language])
        selected-roles    (selected-roles)
        available-roles   (available-role-selector-values target-id selected-roles)
        locked-roles      (if editing-existing?
                            (<sub [::party-common/locked-selected-roles
                                   #(party-edit/reject-remove? target-id editing-id %)])
                            ;; Keep the required role locked until the party exists.
                            (filter #(= required-role-id (:db/id %)) selected-roles))
        process           (<sub [::process/entity-process target-id])
        party-type        (<sub [::party-common/party-type])
        show-errors?      (<sub [::party-common/document [::party-common/show-errors?]])
        invalid?          (validate/field-invalid? show-errors?
                                                   validator
                                                   :party/party-roles)
        invalid-text      (validate/field-invalid-text show-errors?
                                                       validator
                                                       :party/party-roles
                                                       tr)]
    [ui-checkbox-picker/checkbox-picker
     {:id               "party-role-selector"
      :label-text       (if (< 1 (count selected-roles)) (tr [:party/roles]) (tr [:party/role]))
      :values           available-roles
      :selected-items   selected-roles
      :locked-items     locked-roles
      :val->id          :db/id
      :val->str         #(role-model/party-role-name % lang)
      :add-button-text  (tr [:party/role-add])
      :done-text        (tr [:close])
      :invalid?         invalid?
      :invalid-text     invalid-text
      :open-by-default? (empty? selected-roles)
      :disabled?        disabled?
      :on-toggle        (fn [role add?]
                          (if editing-existing?
                            (toggle-edit-role! target-id editing-id role add?)
                            (toggle-create-role! role add? process party-type)))}]))

(defn- role-selector-section [tr validator target editing-id required-role-id party-type]
  [:div.pb-4.mb-4
   (merge {:data-guide-id          "party-roles"
           :data-guide-label       (tr [:party/roles])
           :data-guide-description "Assign one or more roles to this party — open the picker to select from available roles"}
          (when party-type {:class "border-b border-solid border-gray-300"}))
   [role-selector-panel tr validator target editing-id required-role-id (nil? party-type)]])

(defn- party-status-text [tr party]
  (let [invite (-> party :invite/_party first)]
    ;; not-empty: linked parties that were never invited have an empty status,
    ;; which must stay nil here so the card renders no status tag
    (not-empty (party-view/party-status-text tr party invite))))

(defn party-card-status [lang party status-text]
  (cond
    (party-model/removed? party) :inactive
    (not (party-complete? lang party)) :attention
    status-text :task))

(defn- email-link [email]
  (when email
    [:a {:href (str "mailto:" email)} email]))

(defn- expanded-field-rows
  "Returns [label value] pairs for every party field that can be filled in the inline
   edit form and currently holds a value. Mirrors `common-editor-fields-v2`."
  [tr party format-date roles-display]
  (let [{:party/keys [email]
         :user/keys  [corporation-or-partnership mobile-phone other-phone
                      street-address unit-number municipality province postal-code]} (party-model/merge-user-and-party party)
        added-ts                                                                     (or (:entity/created party) (some-> party :invite/_party first :invite/date))]
    (->> [[(tr [:party/role])                              roles-display]
          [(tr [:party/corporation-or-partnership])        corporation-or-partnership]
          [(tr [:party/email])                             (email-link email)]
          [(tr [:party/mobile-phone])                      mobile-phone]
          [(tr [:party/other-phone])                       other-phone]
          [(tr [:party/street-address])                    street-address]
          [(tr [:party/unit-number])                       unit-number]
          [(tr [:party/city])                              municipality]
          [(tr [:party/province])                          province]
          [(tr [:party/postal-code])                       postal-code]
          [(tr [:application-wizard/parties-added])        (some-> added-ts format-date)]]
         (remove (fn [[_ value]] (or (nil? value) (and (string? value) (str/blank? value))))))))

(defn- party-avatar-icon [party]
  (case (party-type party)
    :party.type/business Building32
    :party.type/registered-contractor Construction32
    User32))

(defn- filled-party-card [tr party expanded?]
  (let [lang                         (<sub [:language])
        format-date                  (<sub [::date-util/date-fn])
        party-id                     (:db/id party)
        target-id                    (-> party :entity/parent :db/id)
        active?                      (party-model/active? party)
        can-remove?                  (and (not active?)
                                          (<sub [::process/signal-permitted?
                                                 target-id
                                                 [:process.signal/remove-party {:party-id party-id}]]))
        missing-role-ids             (set (get (<sub [::party-subs/parties-with-missing-required-trade+role-ids target-id])
                                               party))
        roles-display                (if (seq missing-role-ids)
                                       [:div.flex.flex-col.gap-1
                                        (futil/for-frag [role (:party/party-roles party)]
                                          [:div {:class (when (contains? missing-role-ids (:db/id role)) "text-danger")}
                                           (str (role-model/party-role-name role lang)
                                                (when (contains? missing-role-ids (:db/id role)) "*"))])]
                                       (role-pills-display party lang))
        {:party/keys [email]
         :user/keys  [mobile-phone]} (party-model/merge-user-and-party party)
        status-text                  (party-status-text tr party)
        status                       (if (and (seq missing-role-ids) (not (party-model/removed? party)))
                                       :attention
                                       (party-card-status lang party status-text))]
    [ui-card/card
     {:title          (party-model/corporation-and-full-name-or-full-name party lang)
      :avatar         {:icon (party-avatar-icon party)}
      :status         status
      :status-text    status-text
      :header-content [ui-card/slim-button
                       {:icon     View16
                        :on-click #(>evt [::toggle-local-expanded-party-id party-id])}
                       (if expanded?
                         (tr [:application-wizard/parties-hide-details])
                         (tr [:application-wizard/parties-view-details]))]
      :footer-content [ui-card/row {:align :right}
                       (cond
                         active?
                         [ui/button {:kind     :secondary
                                     :size     :small
                                     :icon     i/pen
                                     :on-click #(init-edit-party-state party)}
                          (tr [:edit])]

                         can-remove?
                         [:<>
                          [ui/button {:kind     :danger
                                      :size     :small
                                      :icon     i/trash
                                      :on-click #(>evt [::party-edit/toggle-delete-confirm-modal party-id])}
                           (tr [:party/remove-party])]
                          (when (<sub [::party-edit/delete-confirm-open? party-id])
                            [party-edit/remove-party-modal tr party-id target-id false nil
                             (boolean (:party/user party))])])
                       (when (<sub [::process/signal-permitted?
                                    target-id
                                    [:process.signal/reinvite-party {:party-id party-id}]])
                         [ui/modal-confirmation-button
                          {:id                  (str "wizard-reinvite-party-" party-id)
                           :tr                  tr
                           :trigger-button-text (tr [:party/resend-invite])
                           :trigger-button-opts {:kind :secondary :size :small :icon i/refresh-envelope}
                           :modal-title         (tr [:party/resend-invite])
                           :on-yes-click        #(>evt [::process/signal
                                                        target-id
                                                        [:process.signal/reinvite-party {:party-id party-id}]])}
                          [:div (tr [:party/resend-invite-confirm] [email])]])]}
     (if expanded?
       [:div {:class ["grid gap-x-8 gap-y-2 [grid-template-columns:repeat(auto-fit,minmax(min(100%,16rem),1fr))]"]}
        (futil/for-frag [[label value] (expanded-field-rows tr party format-date roles-display)]
          ^{:key label}
          ;; Wrap so labelled-rows aren't adjacent `data-card-row` siblings; otherwise the
          ;; `[[data-card-row]+&]:mt-3` rule pushes every column's first item down out of alignment.
          [:div [ui-card/labelled-row {:label label} value]])]
       [:<>
        [:div.flex.flex-col.gap-2
         [ui-card/labelled-row {:label (tr [:party/role])} roles-display]
         (when email [ui-card/labelled-row {:label (tr [:party/email])} (email-link email)])
         (when mobile-phone [ui-card/labelled-row {:label (tr [:party/phone])} mobile-phone])]])]))

(defn- applicant-picker
  "Lets a user with businesses choose whether they apply for themselves or for one of their
   businesses. Hidden when the user has no business to choose from."
  [tr phase]
  (let [phase-id  (:db/id phase)
        applicant (<sub [::own-applicant-party phase-id])
        options   (<sub [::applicant-options phase-id])]
    (when (and applicant
               (< 1 (count options))
               (<sub [::process/signal-permitted?
                      phase-id
                      [:process.signal/change-applicant {:party-id (:db/id applicant)}]]))
      (let [selected (<sub [::selected-applicant-option phase-id])]
        [:div.pb-6.border-b.border-solid.border-gray-300.mb-6
         {:data-guide-id                 "applicant-selection"
          :data-guide-label              (tr [:application-wizard/parties-applying-for])
          :data-guide-description        "Choose whether the application is for yourself or one of your businesses"
          :data-guide-highlight-selector "select"}
         [ui-select/select {:id           "wizard-applicant-picker"
                            :label-text   (tr [:application-wizard/parties-applying-for])
                            :required     true
                            :allow-empty? false
                            :values       options
                            :val->str     :name
                            :value        selected
                            :select-text  (tr [:wizard/select-applicant])
                            :on-change    #(when (not= % selected)
                                             (>evt [::select-applicant phase-id (:db/id applicant) %]))}]]))))

(defn- party-type-label [tr party-type]
  (case party-type
    :party.type/registered-contractor (tr [:application-wizard/parties-contractor])
    (tr [party-type])))

(defn- party-type-icon [party-type]
  (case party-type
    :party.type/person i/user
    :party.type/business i/building
    :party.type/registered-contractor i/helmet))

(defn- add-party-dropdown [tr party-types can-add-required-role?]
  [ui-dropdown/dropdown
   {:align                  "right"
    :aria-label             (tr [:party/add])
    :open-icon-description  (tr [:open-menu])
    :close-icon-description (tr [:close-menu])
    :class                  "border-2 border-solid border-ui-violet-100 rounded text-ui-violet-100 hover:bg-ui-violet-100 hover:text-white data-[open=true]:bg-ui-violet-100 data-[open=true]:text-white"
    :toggle-props           {:class "gap-3 px-3 py-2 text-xs font-semibold uppercase cursor-pointer text-inherit"}
    :options-props          {:class "w-max bg-ui-white border border-solid border-gray-300 filter drop-shadow-xl divide-y divide-gray-200"}}
   [:span (tr [:add])]
   (futil/for-frag [party-type party-types]
     ^{:key party-type}
     (cc/button {:kind          :ghost
                 :size          :small
                 :icon          (party-type-icon party-type)
                 :icon-position :left
                 :class-name    "justify-start normal-case whitespace-nowrap w-full text-left text-ui-violet-100 font-normal"
                 :on-click      #(init-add-party-state {:party-type party-type})}
                (party-type-label tr party-type)))
   (when can-add-required-role?
     (cc/button {:kind          :ghost
                 :size          :small
                 :icon          i/user-plus
                 :icon-position :left
                 :class-name    "justify-start normal-case whitespace-nowrap w-full text-left text-ui-violet-100 font-normal"
                 :on-click      #(>evt [::open-add-required-role])}
                (tr [:application-wizard/parties-required-role])))])

(defn- add-required-role-modal [tr phase]
  (let [open?      (some? (<sub [::add-required-role]))
        selected   (<sub [::add-required-role :role])
        phase-id   (:db/id phase)
        roles      (<sub [::party-req-subs/unrequired-party-roles-sorted phase-id])
        role-value (p/find-first roles #(= (:id %) (:id selected)))]
    (cc/composed-modal
      {:open     open?
       :on-close #(>evt [::close-add-required-role])}
      (cc/modal-header {:close-modal #(>evt [::close-add-required-role])
                        :title       (tr [:party/add-required-role])})
      (cc/modal-body
        {}
        [:div.flex-row.pb-8.my-4
         [:div.flex.mb-2
          [:div (tr [:party/choose-add-role])]]
         [:div.flex
          [ui-select/select {:id           "wizard-add-required-role"
                             :class-name   "full-width-select"
                             :select-text  (tr [:party/choose-add-role])
                             :label-text   ""
                             :hide-label   true
                             :value        role-value
                             :on-change    #(>evt [::set-add-required-role %])
                             :val->str     :label
                             :allow-empty? true
                             :values       roles}]]])
      (cc/modal-footer
        {}
        [ui/button {:kind     :secondary
                    :on-click #(>evt [::close-add-required-role])}
         (tr [:close])]
        [ui/button {:kind     :primary
                    :disabled (nil? role-value)
                    :on-click #(>evt [::confirm-add-required-role phase-id])}
         (tr [:confirm])]))))

(defn- party-list-grid [tr phase]
  (let [lang                     (<sub [:language])
        parties                  (<sub [::party-subs/parties (:db/id phase)])
        parties-w-missing-trades (<sub [::party-subs/parties-with-missing-required-trade+role-ids (:db/id phase)])
        missing-roles            (<sub [::missing-roles])
        local-expanded-ids       (<sub [::local-expanded-party-ids])
        display-business-option? (<sub [::process/signal-permitted? (:db/id phase) [:process.signal/add-new-business]])
        display-cr-option?       (some :party-role/trade (<sub [::ws-subs/sorted-party-roles]))
        can-add-required-role?   (<sub [::party-req-subs/can-add-role-requirement? (:db/id phase)])
        available-party-types    (add-party-type-values display-cr-option? display-business-option?)
        process                  (<sub [::process/entity-process (:db/id phase)])
        ;; Types this role can be added as; used to preselect when there is only one.
        compatible-party-types   (fn [role]
                                   (role-compatible-party-types process available-party-types (:db/id role)))
        missing-role-ids         (set (map :db/id missing-roles))
        missing-requirements     (->> (req-model/phase-role-requirements phase)
                                      ;;FIXME filtered out for TT-27119, remove this when wizard supports deferring
                                      (remove #(= (:requirement/kind %) :requirement.kind/deferred-role))
                                      (filter #(contains? missing-role-ids
                                                          (:db/id (:requirement/party-role %)))))
        party-ids                (map :db/id parties)
        all-expanded?            (and (seq party-ids) (every? local-expanded-ids party-ids))]
    [:div
     [:div.flex.justify-between.items-start.mb-6
      ;; m-0: no Tailwind base reset, so UA h4 margin would gap this row from the description.
      [:h4.text-sm.font-semibold.m-0 (tr [:application-wizard/parties-persons-businesses])]
      [add-party-dropdown tr available-party-types can-add-required-role?]]
     (when (seq parties-w-missing-trades)
       [:p.mb-6.text-sm {:role "status"}
        (tr [:party/party-trades-missing-info])])
     (when (seq parties)
       (let [[label-to-show icon-to-show]
             (if all-expanded?
               [(tr [:application-wizard/parties-collapse-all]) CollapseAll16]
               [(tr [:application-wizard/parties-expand-all]) ExpandAll16])]
         [:div.flex.justify-end.mb-6
          [:button.inline-flex.items-center.gap-1.text-xs.font-semibold.text-ui-violet-100.uppercase.bg-transparent.border-none.cursor-pointer
           {:on-click #(>evt [::set-all-parties-expanded party-ids (not all-expanded?)])}
           label-to-show
           [:> icon-to-show {:title label-to-show}]]]))

     [:div.grid.grid-cols-1.md:grid-cols-2.lg:grid-cols-3.gap-6
      ;; Equal row heights so a lone card on the last row matches the others;
      ;; disabled while a card is expanded so its tall row doesn't inflate the rest.
      {:class                  (when (not-any? local-expanded-ids party-ids) "auto-rows-fr")
       :data-guide-id          "party-cards"
       :data-guide-label       (tr [:application-wizard/parties-persons-businesses])
       :data-guide-description (str "Party cards: edit or remove parties and resend invitations when permitted. "
                                    "Roles marked with * are missing a required contractor trade.")}
      (futil/for-frag [party parties
                       :let  [expanded? (contains? local-expanded-ids (:db/id party))]]
        ^{:key (:db/id party)}
        ;; Expanded cards span the full grid width so their details get room to
        ;; breathe; h-full so the card stretches to the grid row height.
        [:div {:class ["h-full" (when expanded? "md:col-span-2 lg:col-span-3")]}
         [filled-party-card tr party expanded?]])

      (futil/for-frag [requirement missing-requirements
                       :let        [role (:requirement/party-role requirement)]]
        ^{:key (str "placeholder-" (:db/id requirement))}
        [placeholder-card tr
         phase
         requirement
         (role-model/party-role-name role lang)
         (compatible-party-types role)])]]))

(rf/reg-event-fx ::submit-person-party-success
  (fn [_ [_ operation-id close-inline-form _status _response]]
    {:dispatch-n [[::save-tracking/save-succeeded operation-id]
                  close-inline-form]}))

(defn- existing-party-for-email [phase email]
  (when-not (str/blank? email)
    (let [email (normalize/canonicalize-email email)]
      (-> phase
          party-model/phase-parties
          (p/find-first #(= email (normalize/canonicalize-email (party-model/unchecked-user-party-email %))))))))

(rf/reg-event-fx ::submit-party
  (fn [{:keys [db]} [_ tr target party-type]]
    (let [document          (::party-common/document db)
          manual?           (::party-common/manual-mode? document)
          existing-party    (when (= party-type :party.type/person)
                              (existing-party-for-email
                                (entities/find-entity (entities/db->context db) (:db/id target))
                                (:party/email document)))
          close-inline-form [::close-inline-form]]
      (when-not existing-party
        {:fx [[:dispatch (case party-type
                           :party.type/business
                           [::party-business/add-business target {:on-success close-inline-form}]

                           :party.type/registered-contractor
                           [::party-registered-contractor/add-existing-registration
                            target
                            {:on-success close-inline-form}]

                           (let [operation-id (random-uuid)]
                             [::party-common/submit-clicked
                              tr
                              (:db/id target)
                              {:on-start        [::save-tracking/save-started operation-id]
                               :on-success      [::submit-person-party-success operation-id close-inline-form]
                               :on-failure      [::save-tracking/save-failed operation-id]
                               :success-message (if manual? :party/added :party/invited)}]))]]}))))

(defn- inline-party-form-footer [tr target editing-existing? editing-id party-type add-valid? invite-only?]
  [:div.flex.justify-end.items-center.mt-4.pt-4.border-t.border-solid.border-gray-300
   [:div.flex.gap-4
    (when editing-existing?
      [ui/button {:kind     :danger
                  :size     :small
                  :disabled (not (<sub [::process/signal-permitted?
                                        (:db/id target)
                                        [:process.signal/remove-party {:party-id editing-id}]]))
                  :on-click #(>evt [::party-edit/toggle-delete-confirm-modal editing-id])}
       (tr [:party/remove-party])])
    (if editing-existing?
      [ui/button {:kind     :primary
                  :size     :small
                  :on-click #(>evt [::close-inline-form])}
       (tr [:close])]
      [:<>
       [ui/button {:kind     :secondary
                   :size     :small
                   :on-click #(>evt [::close-inline-form])}
        (tr [:cancel])]
       [ui/button {:kind     :primary
                   :size     :small
                   :disabled (not add-valid?)
                   :on-click #(>evt [::submit-party tr target party-type])}
        (tr [(if invite-only? :party/send-button :party/add-button)])]])]])

(defn- contractor-display-name [party lang]
  (if-let [registration (:party/registration party)]
    [party-registered-contractor/registration-display-name lang registration]
    (party-model/corporation-and-full-name-or-full-name party lang)))

(defn- refilter-party-type-roles! [new-party-type current-roles process]
  (let [new-roles (party-common/permitted-party-role-ids current-roles
                                                         process
                                                         :invite
                                                         new-party-type)]
    (>evt [::party-common/set-doc-value [:party/type] new-party-type])
    (when (= new-party-type :party.type/registered-contractor)
      (>evt [::party-registered-contractor/init]))
    (>evt [::party-common/set-doc-value [:party/party-roles] new-roles])))

(def ^:private form-field-classes
  "Wizard-specific restyle of the Carbon form fields rendered by the reused party form
   components: darker, slightly larger labels and fully bordered, rounded inputs.
   Applies to text, email and phone inputs as well as selects (business form)."
  (str "[&_.bx--label]:!text-sm [&_.bx--label]:!text-ui-night-sky-100 "
       "[&_.bx--text-input]:!border [&_.bx--text-input]:!border-solid "
       "[&_.bx--text-input]:!border-ui-night-sky-50 [&_.bx--text-input]:!rounded "
       "[&_.bx--select-input]:!border [&_.bx--select-input]:!border-solid "
       "[&_.bx--select-input]:!border-ui-night-sky-50 [&_.bx--select-input]:!rounded"))

(defn- business-field-value [business field]
  (case field
    :business/names       (some-> (:business/names business) i18n-model/strings-map)
    :business/trade-names (some-> (:business/trade-names business) i18n-model/strings-map)
    (get business field)))

(defn- comparable-business-field-value [value]
  (when-not (and (string? value) (str/blank? value))
    value))

(defn business-field-save-needed?
  "Return true when the projected business field value should be autosaved. Public for focused tests."
  [validate business field value]
  (and (not= (comparable-business-field-value value)
             (comparable-business-field-value (business-field-value business field)))
       (not (validate field value))))

(defn- save-business-field! [validate target editing-id business field value]
  ;; Skip unless the value actually changed and is valid - generic inputs fire on blur even when
  ;; untouched. (An unchanged masked personal-id equals the live value, so it's skipped here too.)
  (when (business-field-save-needed? validate business field value)
    (>evt [::process/signal
           (:db/id target)
           [:process.signal/edit-business
            {:party-id   editing-id
             :field-path field
             :new-value  value}]
           {:track-save? true}])))

(defn- editable-business-form
  "Editable business form for an existing business party in the wizard. Reuses the shared
   `business-info-form`, auto-saving each field via the :process.signal/edit-business signal:
   selects save on change, text/phone on blur and localized strings on end-of-edit."
  [tr target editing-id document]
  (let [business    (<sub [::entities/entity (:db/id (:party/business document))])
        validate    (<sub [::party-business/edit-field-validator-fn])
        update-doc! (fn [attr v]
                      (if (or (nil? v) (and (string? v) (str/blank? v)))
                        (rf/dispatch-sync [::party-common/dissoc-doc-path [attr]])
                        (rf/dispatch-sync [::party-common/set-doc-value [attr] v])))]
    [business-info-form/business-info-form
     {:tr                tr
      :validator         (<sub [::party-business/edit-validator-fn])
      :schema            (<sub [::party-business/edit-schema])
      :existing-business business
      :light             true
      :value-fn          #(<sub [::party-common/document [%]])
      ;; Mirror every change into the doc (selects/cascades render from it), then autosave.
      :on-change         (fn [attr v]
                           (update-doc! attr v)
                           (case attr
                             ;; Changing country clears its dependent subdivision, so don't save
                             ;; country alone (would persist a country/subdivision mismatch) -
                             ;; defer until subdivision is reselected below.
                             :business/mailing-country
                             nil

                             ;; Save the deferred country together with the subdivision
                             ;; (country save is a no-op when unchanged).
                             :business/subdivision
                             (when v
                               (save-business-field! validate target editing-id business
                                                     :business/mailing-country
                                                     (<sub [::party-common/document [:business/mailing-country]]))
                               (save-business-field! validate target editing-id business :business/subdivision v))

                             (save-business-field! validate target editing-id business attr v)))
      :on-change-sync    (fn [attr v]
                           (if (and (string? v) (str/blank? v))
                             (rf/dispatch-sync [::party-common/dissoc-doc-path [attr]])
                             (rf/dispatch-sync [::party-common/set-doc-value [attr] v])))
      :on-save-needed    (fn [field value]
                           (save-business-field! validate target editing-id business field value))}]))

;; Inline edit/creation form view implementation
(defn- inline-party-form [tr target editing]
  (let [editing-existing?        (not (editing-creating? editing))
        editing-id               (editing-party-id editing)
        fixed-contractor-role?   (:fixed-contractor-role? editing)
        document                 (<sub [::party-common/document])
        party-type               (if editing-existing?
                                   (:party-type editing)
                                   ;; Avoid ::party-common/party-type: it defaults to person before a choice.
                                   (:party/type document))
        invite-only?             (and (not editing-existing?)
                                      (= party-type :party.type/person)
                                      (not (::party-common/manual-mode? document)))
        existing-party           (when (and (not editing-existing?) (= party-type :party.type/person))
                                   (existing-party-for-email target (:party/email document)))
        display-business-option? (<sub [::process/signal-permitted? (:db/id target) [:process.signal/add-new-business]])
        ;; Offer the contractor-registry option whenever the workspace has any applicable trade role,
        ;; not only when such a role is a required requirement of the phase — an optional contractor
        ;; can still be added. The actual add is gated downstream by party-operation-permitted?.
        display-cr-option?       (some :party-role/trade (<sub [::ws-subs/sorted-party-roles]))
        ;; Limit radios to types the required role can actually be added on.
        required-role-id         (:required-role-id editing)
        required-role            (when required-role-id
                                   (p/find-first (<sub [::ws-subs/party-roles]) {:db/id required-role-id}))
        process                  (<sub [::process/entity-process (:db/id target)])
        party-type-values        (if fixed-contractor-role?
                                   [:party.type/registered-contractor]
                                   (let [types (add-party-type-values display-cr-option? display-business-option?)]
                                     (if required-role-id
                                       (role-compatible-party-types process types required-role-id)
                                       types)))
        validator                (if editing-existing?
                                   #(validate/validate-field % (<sub [::party-common/document]) party-common/form-model)
                                   (<sub [::party-common/validator]))
        current-roles            (<sub [::party-common/document-party-roles])
        registration             (<sub [::party-common/party-registration])
        contractor-role-values   (when (and (not editing-existing?)
                                            (= party-type :party.type/registered-contractor)
                                            (not fixed-contractor-role?))
                                   (available-role-selector-values (:db/id target) current-roles))
        extra-perms              (-> target
                                     :entity/parent
                                     (party-edit/available-extra-permissions
                                       (boolean (or (:party/user document) (:party/business document)))
                                       (:entity/type target)))
        add-valid?               (or editing-existing?
                                     (and party-type
                                          (not existing-party)
                                          (case party-type
                                            :party.type/business (<sub [::party-business/form-valid?])
                                            ;; role-selected? alone doesn't guarantee a contractor was actually picked
                                            ;; from search results — without it, :party/registration stays nil and
                                            ;; submit would dispatch add-existing-contractor with a nil registration id.
                                            :party.type/registered-contractor (and (<sub [::party-registered-contractor/role-selected?])
                                                                                   (some? (<sub [::party-common/party-registration])))
                                            (<sub [::party-common/form-valid?]))))]
    [:div.p-4.bg-white.rounded.border.border-solid.border-gray-400.mt-4
     {:class form-field-classes}
     (when (and (not editing-existing?) required-role)
       [:div.flex.justify-between.items-center.pb-4
        [:span.font-semibold.text-sm.text-ui-night-sky-100
         (role-model/party-role-name required-role (<sub [:language]))]
        [ui-status-tag/tag {:status :attention} (tr [:application-wizard/parties-required])]])

     (when (and (not editing-existing?) (> (count party-type-values) 1))
       [:div.pb-6.border-b.border-solid.border-gray-300.mb-6
        {:data-guide-id                 "party-type"
         :data-guide-label              (tr [:application-wizard/parties-party-type])
         :data-guide-description        "Choose the type of party to add: person, contractor, or business"
         :data-guide-highlight-selector ".bx--radio-button__label"}
        [:h4.text-sm.font-semibold.mt-0.mb-3 (tr [:application-wizard/parties-party-type])]
        [ui-radio/radio (cond-> {:name      "new-party-type"
                                 :values    party-type-values
                                 :value     party-type
                                 :val->str  #(party-type-label tr %)
                                 :on-change #(refilter-party-type-roles! % current-roles process)}
                          ;; Add menu already chose the type; placeholder flow keeps radios enabled.
                          (and party-type (not required-role-id))
                          (assoc :disabled-values (set party-type-values)))]])

     (when (not= party-type :party.type/registered-contractor)
       [role-selector-section tr validator target editing-id required-role-id party-type])

     (when party-type
       [:div
        (case party-type
          :party.type/business
          (let [business           (:party/business document)
                ;; Auth lives in the action (:permission.business/write), not the phase guard, so the
                ;; form checks that permission AND the signal guard (which needs :party-id).
                can-edit-business? (and editing-existing?
                                        (contains? (:granted-permissions business) :permission.business/write)
                                        (<sub [::process/signal-permitted?
                                               (:db/id target)
                                               [:process.signal/edit-business {:party-id editing-id}]]))]
            (cond
              can-edit-business? [editable-business-form tr target editing-id document]
              editing-existing?  [business-info-form/read-only-form tr business]
              :else              [party-business/business-form tr target {:light true :hide-role-selector? true}]))

          :party.type/registered-contractor
          (if editing-existing?
            [:div.flex.flex-col.gap-2
             [:h4.text-sm.font-semibold.mt-0.mb-1 (tr [:application-wizard/parties-contractor])]
             [:div (contractor-display-name document (<sub [:language]))]
             [role-selector-section tr validator target editing-id required-role-id party-type]]
            [:<>
             [party-registered-contractor/contractor-search
              tr
              target
              {:hide-role-selector? true
               :on-select           (when-not fixed-contractor-role?
                                      #(>evt [::select-contractor % contractor-role-values required-role-id]))}]
             (when (and registration (not fixed-contractor-role?))
               [role-selector-section tr validator target editing-id required-role-id party-type])])

          [:div {:data-guide-id                 "personal-details"
                 :data-guide-label              (tr [:application-wizard/parties-personal-details])
                 :data-guide-description        "Enter the party's name and contact information"
                 :data-guide-highlight-selector "input"}
           [:form.flex.flex-col.w-full
            {:on-submit (fn [event]
                          (.preventDefault event)
                          (when (and (not editing-existing?) add-valid?)
                            (>evt [::submit-party tr target party-type])))}
            [:fieldset
             [:h4.text-sm.font-semibold.mt-0.mb-3 (tr [:application-wizard/parties-personal-details])]
             (when (and (not editing-existing?) (not existing-party))
               [:div.mb-4
                {:data-guide-id                 "invite-only"
                 :data-guide-label              (tr [:party/input-manually])
                 :data-guide-description        "Enter personal details manually instead of inviting by email only"
                 :data-guide-highlight-selector "input"}
                [cc/checkbox {:id         "add-party-manually-checkbox"
                              :label-text (tr [:party/input-manually])
                              :checked    (boolean (::party-common/manual-mode? document))
                              :on-change  #(rf/dispatch-sync
                                             [::party-common/set-doc-value
                                              [::party-common/manual-mode?] %])}]])
             (cond
               editing-existing?
               [party-common/common-editor-fields-v2
                (util/mmap {:disabled?       (fn [field]
                                               (not (<sub [::process/signal-permitted?
                                                           (:db/id target)
                                                           [:process.signal/edit-party
                                                            (p/assoc-if {:party-id editing-id}
                                                                        :field-path field)]])))
                            :on-save-needed  (fn [field value]
                                               (>evt [::party-edit/save
                                                      field
                                                      value
                                                      editing-id
                                                      (:db/id target)
                                                      (when (= field :party/email)
                                                        {:party/apply-default-invite-permissions? true})]))
                            :deferred-email? true
                            :show-email?     true
                            :light           true}
                           tr validator)]

               invite-only?
               [party-common/email-input {:tr        tr
                                          :validator validator
                                          :field     :party/email
                                          :label     :party/email
                                          :required? true
                                          :light     true}]

               :else
               [party-common/common-editor-fields-v2
                {:tr              tr
                 :validator       validator
                 :show-email?     true
                 ;; plain email input — the deferred edit flow with its invite-resend hint
                 ;; does not apply in the wizard
                 :deferred-email? false
                 :light           true}])

             (when existing-party
               [:div.mt-4 {:role "alert"}
                [:div.font-bold.text-danger (tr [:party/existing-email-warning])]
                (if (party-model/active? existing-party)
                  [cc/link {:href     "#"
                            :on-click (fn [event]
                                        (.preventDefault event)
                                        (init-edit-party-state existing-party))}
                   (tr [:party/edit-existing])]
                  [:div (tr [:party/delete-existing])])])

             (when (and (seq extra-perms) (not editing-existing?))
               [:div.mt-6
                [party-common/additional-permissions
                 (util/mmap {:permissions extra-perms} tr target)]])]]])])

     [inline-party-form-footer tr target editing-existing? editing-id party-type add-valid? invite-only?]

     (when (and editing-existing? (<sub [::party-edit/delete-confirm-open? editing-id]))
       (let [user-id         (<sub [:session/user-id])
             remove-self?    (<sub [::party-subs/only-party-of-user? (:db/id target) editing-id])
             party-has-user? (some? (:party/user document))]
         [party-edit/remove-party-modal tr editing-id (:db/id target) remove-self? user-id party-has-user?
          {:on-success [::close-inline-form]}]))]))

(defn view [tr]
  (let [phase   (<sub [::phase])
        editing (<sub [::editing-state])]
    [:div#application-wizard--parties
     {:data-guide-page-description
      "Application wizard — Parties step: Add and manage parties (applicants, contractors, businesses) required for the application. Each party must be assigned at least one role. Use a placeholder card dropdown to assign an existing party, add a new party, or remove a required role."}
     (if phase
       (if editing
         [inline-party-form tr phase editing]
         [:<>
          [applicant-picker tr phase]
          [party-list-grid tr phase]
          [assign-role/assign-modal tr phase (<sub [::party-subs/parties (:db/id phase)])]
          [add-required-role-modal tr phase]
          [party-requirements/remove-role-requirement-modal tr]])
       [:div.p-6 (tr [:loading])])]))
